import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Component, computed, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import {
  lucideArrowDown,
  lucideArrowUp,
  lucideEllipsisVertical,
  lucideFolderSearch,
  lucidePen,
  lucidePlus,
  lucideSearch,
  lucideTrash,
} from '@ng-icons/lucide';
import { ExpansionPanelWithoutBorder } from '@shared/components/expansion-panel-without-border/expansion-panel-without-border.component';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { DialogConfirmDeleteComponent } from '../../../../shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { ConfirmDialogComponent } from '../../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ExpansionPanelMenuAction } from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { FiltrerParPipe } from '../../../../shared/pipes/filtrer-par.pipe';
import { TrierParPipe } from '../../../../shared/pipes/trier-par.pipe';
import { FormAptitudesComponent } from '../../processus-aptitude/form/form-aptitudes/form-aptitudes.component';
import { ItemAptitudeProcessusComponent } from '../../processus-aptitude/item-aptitude-processus/item-aptitude-processus.component';
import { Aptitude } from '../../processus-aptitude/models/aptitude.model';
import { AptitudeStore } from '../../processus-aptitude/store/aptitude.store';
import { FormPhaseComponent } from '../forms/form-phase/form-phase.component';
import { Phase } from '../models/phase.model';
import { PhaseStore } from '../store/phase.store';
import { ActivatedRoute } from '@angular/router';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-item-phase',
  imports: [
    ExpansionPanelWithoutBorder,
    ChipComponent,
    HlmIconImports,
    HlmButtonImports,
    CdkDrag,
    CdkDropList,
    ItemAptitudeProcessusComponent,
    FiltrerParPipe,
    TrierParPipe,
  ],
  templateUrl: './item-phase.component.html',
  providers: [
    provideIcons({
      lucidePlus,
      lucideTrash,
      lucideSearch,
      lucidePen,
      lucideEllipsisVertical,
      lucideArrowUp,
      lucideArrowDown,
      lucideFolderSearch,
    }),
  ],
})
export class ItemPhaseComponent {
  readonly #phaseStore = inject(PhaseStore);
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #route = inject(ActivatedRoute);
  protected readonly aptitudesIdsPhases = computed(() =>
    this.#phaseStore.ids().map((x: { toString: () => string }) => 'aptitudes-phase-' + x.toString()),
  );

  readonly aptitudeLabel = computed(() => 'A:' + this.phase().aptitudes.length);

  readonly phase = input.required<Phase>();
  readonly phases = input.required<Phase[]>();
  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly peutModifier = input.required<boolean>();

  readonly onCreateIndiceResultat = output<Aptitude | undefined>();
  readonly onCreateProduction = output<Aptitude | undefined>();

  readonly phasesActions: ExpansionPanelMenuAction<Phase>[] = [
    {
      icon: 'lucidePen',
      label: 'Modifier le nom',
      action: (phase: Phase | undefined) => this.#modifierPhase(phase),
    },
    {
      icon: 'lucideTrash',
      label: 'Supprimer la phase',
      action: (phase: Phase | undefined) => this.#supprimerPhase(phase),
    },
    {
      icon: 'lucidePlus',
      label: 'Ajouter une aptitude',
      action: (phase: Phase | undefined) => this.ajoutAptitude(phase),
    },
    {
      icon: 'lucideArrowUp',
      label: 'Faire monter',
      action: (phase: Phase | undefined) => this.#faireMonter(phase, true),
    },
    {
      icon: 'lucideArrowDown',
      label: 'Faire descendre',
      action: (phase: Phase | undefined) => this.#faireMonter(phase, false),
    },
  ];

  ajoutAptitude(phase?: Phase) {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une nouvelle aptitude',
      FormAptitudesComponent,
      'Créez une ou plusieurs aptitudes, et ajoutez-les les unes sous les autres.',
      {
        phase,
        estCreation: true,
        cartographie: this.cartographie(),
      },
    );
    this.#sidepanelService.open();
  }

  #faireMonter(phase: Phase | undefined, isUp: boolean): void {
    if (!phase) return;

    this.#phaseStore.modifier({
      idPhase: phase.id,
      data: {
        nom: phase.nom,
        indice: isUp ? phase.indice - 1 : phase.indice + 1,
        idReferentiel: this.referentiel().id,
      },
      next: () => {
        this.#phaseStore.charger(this.referentiel().id);
      },
    });
  }

  #supprimerPhase(phase: Phase | undefined): void {
    if (!phase) return;
    let dialogRef;

    const nbrAptitudes = phase.aptitudes.length;
    const nbrIndices = phase.aptitudes.flatMap((x) => x.indicesResultats).length;
    const nbrIndicateurs = phase.aptitudes
      .flatMap((x) => x.indicesResultats)
      .flatMap((x) => x.indicateursPerformance).length;

    if (nbrAptitudes > 0) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: phase.nom,
          childrenText: `${nbrAptitudes} aptitudes, ${nbrIndices} indices et ${nbrIndicateurs} indicateurs liés`,
          typeElement: 'la phase',
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Supprimer la phase`,
          contenu: `Êtes-vous sûr de vouloir supprimer la phase ${phase.nom} ?`,
          confirmBtnText: `Confirmer la suppression`,
        },
      });
    }
    const toastrId = uuidv7();

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#phaseStore.supprimer({
          idPhase: phase.id,
          next: () => {
            toast.custom(ToastrSuccessComponent, {
              componentProps: { message: 'Suppression de la phase réussie', id: toastrId },
              id: toastrId,
            });
            this.#phaseStore.charger(this.referentiel().id);
          },
        });
      }
    });
  }

  #modifierPhase(phase: Phase | undefined): void {
    if (!phase) return;

    this.#sidepanelService.setComponentToDisplay(
      'Modifier la phase',
      FormPhaseComponent,
      `Modifiez le nom de votre phase. Si besoin de modifier les aptitudes rattachées, cliquez sur “gérer les aptitudes” dans le sous-menu de la phase.`,
      {
        phase: phase,
      },
    );
    this.#sidepanelService.open();
  }

  dropAptitude(event: CdkDragDrop<Aptitude[]>, idNouvellePhase: string) {
    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) return;

    const toastrId = uuidv7();

    const aptitude = event.item.data as Aptitude;
    if (event.previousContainer === event.container) {
      aptitude.indice = event.currentIndex;
      this.#aptitudeStore.modifier({
        idAptitude: aptitude.id,
        data: {
          ...aptitude,
          idReferentiel: this.referentiel().id,
        },
        next: () => {
          this.#phaseStore.charger(this.referentiel().id);
          toast.custom(ToastrSuccessComponent, {
            componentProps: { message: 'Réordonnancement des aptitudes réussi', id: toastrId },
            id: toastrId,
          });
        },
      });
    } else {
      aptitude.indice = event.currentIndex;
      aptitude.idPhase = idNouvellePhase;
      this.#aptitudeStore.modifier({
        idAptitude: aptitude.id,
        data: {
          id: aptitude.id,
          idPhase: idNouvellePhase,
          nom: aptitude.nom,
          idReferentiel: this.referentiel().id,
          indice: event.currentIndex,
        },
        next: () => {
          this.#phaseStore.charger(this.referentiel().id);
          toast.custom(ToastrSuccessComponent, {
            componentProps: { message: 'Réordonnancement des aptitudes réussi', id: toastrId },
            id: toastrId,
          });
        },
      });
    }
  }

  hasOneAptitudeSelected() {
    const idAptitude = this.#route.snapshot.queryParamMap.get('idAptitude');
    const found = this.phase().aptitudes.find((x) => x.id === idAptitude);
    return found != undefined;
  }
}
